import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Chip,
  Grid,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useForm, Controller } from 'react-hook-form';
import { paymentsApi, Payment, CreatePaymentData, UpdatePaymentData, PaymentFilters } from '../services/payments';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';

const Payments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [viewingPayment, setViewingPayment] = useState<Payment | null>(null);
  const [paymentToDelete, setPaymentToDelete] = useState<Payment | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'invoice' | 'payroll_contribution'>('all');

  const paymentForm = useForm<UpdatePaymentData>({
    defaultValues: {
      amount: 0,
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      method: 'bank_transfer',
      reference: '',
      notes: '',
    },
  });

  useEffect(() => {
    loadPayments();
  }, [filterType]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: PaymentFilters = {};
      if (filterType !== 'all') {
        filters.paymentType = filterType;
      }
      const paymentsData = await paymentsApi.getAll(filters);
      setPayments(paymentsData || []);
    } catch (err: any) {
      console.error('Error loading payments:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load payments';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (payment: Payment) => {
    setEditingPayment(payment);
    paymentForm.reset({
      amount: payment.amount,
      paymentDate: format(parseISO(payment.paymentDate), 'yyyy-MM-dd'),
      method: payment.method,
      reference: payment.reference || '',
      notes: payment.notes || '',
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingPayment(null);
    paymentForm.reset();
  };

  const handleOpenViewDialog = (payment: Payment) => {
    setViewingPayment(payment);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingPayment(null);
  };

  const handleOpenDeleteDialog = (payment: Payment) => {
    setPaymentToDelete(payment);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPaymentToDelete(null);
  };

  const handleUpdatePayment = async (data: UpdatePaymentData) => {
    if (!editingPayment) return;

    try {
      setError(null);
      await paymentsApi.update(editingPayment.id, data);
      handleCloseEditDialog();
      loadPayments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update payment');
    }
  };

  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;

    try {
      setError(null);
      await paymentsApi.delete(paymentToDelete.id);
      handleCloseDeleteDialog();
      loadPayments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete payment');
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      bank_transfer: 'Bank Transfer',
      card: 'Card',
      cash: 'Cash',
      check: 'Check',
      other: 'Other',
    };
    return labels[method] || method;
  };

  const getPaymentTypeLabel = (type?: string) => {
    if (!type) return 'Invoice';
    return type === 'payroll_contribution' ? 'Payroll Contribution' : 'Invoice';
  };

  if (loading && payments.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Payments</Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Type</InputLabel>
          <Select
            value={filterType}
            label="Filter by Type"
            onChange={(e) => setFilterType(e.target.value as 'all' | 'invoice' | 'payroll_contribution')}
          >
            <MenuItem value="all">All Payments</MenuItem>
            <MenuItem value="invoice">Invoice Payments</MenuItem>
            <MenuItem value="payroll_contribution">Payroll Contributions</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">No payments found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => {
                  const isOutgoing = payment.direction === 'outgoing';
                  return (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {format(parseISO(payment.paymentDate), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={isOutgoing ? 'Outgoing Payment' : 'Incoming Payment'}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {isOutgoing ? (
                              <ArrowUpwardIcon 
                                color="error" 
                                fontSize="small"
                                sx={{ fontSize: '1.2rem' }}
                              />
                            ) : (
                              <ArrowDownwardIcon 
                                color="success" 
                                fontSize="small"
                                sx={{ fontSize: '1.2rem' }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getPaymentTypeLabel(payment.paymentType)}
                          size="small"
                          color={payment.paymentType === 'payroll_contribution' ? 'secondary' : 'primary'}
                        />
                      </TableCell>
                    <TableCell>
                      {payment.paymentType === 'payroll_contribution' ? (
                        <>
                          {payment.employeeName && (
                            <Typography variant="body2">
                              {payment.employeeName}
                            </Typography>
                          )}
                          {payment.payPeriodStart && payment.payPeriodEnd && (
                            <Typography variant="caption" color="textSecondary">
                              {format(parseISO(payment.payPeriodStart), 'MMM d')} -{' '}
                              {format(parseISO(payment.payPeriodEnd), 'MMM d, yyyy')}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <>
                          {payment.invoiceNumber && (
                            <Typography variant="body2">
                              Invoice: {payment.invoiceNumber}
                            </Typography>
                          )}
                          {payment.clientName && (
                            <Typography variant="caption" color="textSecondary">
                              {payment.clientName}
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(
                          payment.amount,
                          payment.paymentType === 'payroll_contribution'
                            ? payment.payslipCurrency || 'USD'
                            : payment.invoiceCurrency || 'USD'
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>{getPaymentMethodLabel(payment.method)}</TableCell>
                    <TableCell>{payment.reference || '-'}</TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenViewDialog(payment)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(payment)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(payment)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* View Payment Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Payment Details</DialogTitle>
        <DialogContent>
          {viewingPayment && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Direction</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {viewingPayment.direction === 'outgoing' ? (
                      <>
                        <ArrowUpwardIcon color="error" fontSize="small" />
                        <Typography variant="body1">Outgoing Payment</Typography>
                      </>
                    ) : (
                      <>
                        <ArrowDownwardIcon color="success" fontSize="small" />
                        <Typography variant="body1">Incoming Payment</Typography>
                      </>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Payment Type</Typography>
                  <Chip
                    label={getPaymentTypeLabel(viewingPayment.paymentType)}
                    size="small"
                    color={viewingPayment.paymentType === 'payroll_contribution' ? 'secondary' : 'primary'}
                  />
                </Grid>
                {viewingPayment.paymentType === 'payroll_contribution' ? (
                  <>
                    {viewingPayment.employeeName && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">Employee</Typography>
                        <Typography variant="body1">{viewingPayment.employeeName}</Typography>
                      </Grid>
                    )}
                    {viewingPayment.payPeriodStart && viewingPayment.payPeriodEnd && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">Pay Period</Typography>
                        <Typography variant="body1">
                          {format(parseISO(viewingPayment.payPeriodStart), 'MMM d, yyyy')} -{' '}
                          {format(parseISO(viewingPayment.payPeriodEnd), 'MMM d, yyyy')}
                        </Typography>
                      </Grid>
                    )}
                  </>
                ) : (
                  <>
                    {viewingPayment.invoiceNumber && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">Invoice Number</Typography>
                        <Typography variant="body1">{viewingPayment.invoiceNumber}</Typography>
                      </Grid>
                    )}
                    {viewingPayment.clientName && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">Client</Typography>
                        <Typography variant="body1">{viewingPayment.clientName}</Typography>
                      </Grid>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {formatCurrency(
                      viewingPayment.amount,
                      viewingPayment.paymentType === 'payroll_contribution'
                        ? viewingPayment.payslipCurrency || 'USD'
                        : viewingPayment.invoiceCurrency || 'USD'
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Payment Date</Typography>
                  <Typography variant="body1">
                    {format(parseISO(viewingPayment.paymentDate), 'MMM d, yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Payment Method</Typography>
                  <Typography variant="body1">
                    {getPaymentMethodLabel(viewingPayment.method)}
                  </Typography>
                </Grid>
                {viewingPayment.reference && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Reference</Typography>
                    <Typography variant="body1">{viewingPayment.reference}</Typography>
                  </Grid>
                )}
                {viewingPayment.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                    <Typography variant="body1">{viewingPayment.notes}</Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Created: {format(parseISO(viewingPayment.createdAt), 'MMM d, yyyy HH:mm')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <form onSubmit={paymentForm.handleSubmit(handleUpdatePayment)}>
          <DialogTitle>Edit Payment</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                required
                {...paymentForm.register('amount', {
                  required: 'Amount is required',
                  valueAsNumber: true,
                  min: { value: 0, message: 'Amount must be positive' },
                })}
                error={!!paymentForm.formState.errors.amount}
                helperText={paymentForm.formState.errors.amount?.message}
              />
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                required
                {...paymentForm.register('paymentDate', { required: 'Payment date is required' })}
                InputLabelProps={{ shrink: true }}
                error={!!paymentForm.formState.errors.paymentDate}
                helperText={paymentForm.formState.errors.paymentDate?.message}
              />
              <Controller
                name="method"
                control={paymentForm.control}
                rules={{ required: 'Payment method is required' }}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth required error={!!fieldState.error}>
                    <InputLabel>Payment Method</InputLabel>
                    <Select {...field} label="Payment Method">
                      <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                      <MenuItem value="card">Card</MenuItem>
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="check">Check</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              <TextField
                fullWidth
                label="Reference (Optional)"
                {...paymentForm.register('reference')}
                error={!!paymentForm.formState.errors.reference}
                helperText={paymentForm.formState.errors.reference?.message}
              />
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                {...paymentForm.register('notes')}
                error={!!paymentForm.formState.errors.notes}
                helperText={paymentForm.formState.errors.notes?.message}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button type="submit" variant="contained">Update Payment</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Payment Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Payment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this payment?
            <br />
            <br />
            Amount: {paymentToDelete ? formatCurrency(
              paymentToDelete.amount,
              paymentToDelete.paymentType === 'payroll_contribution'
                ? paymentToDelete.payslipCurrency || 'USD'
                : paymentToDelete.invoiceCurrency || 'USD'
            ) : ''}
            <br />
            Date: {paymentToDelete ? format(parseISO(paymentToDelete.paymentDate), 'MMM d, yyyy') : ''}
            <br />
            <br />
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeletePayment} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payments;
